/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppConfig, useLocale, QUERY_PERSIST_CACHE } from '@airasia-rn/ui';
import { useAccessToken, useIsLoggedIn } from '@airasia-rn/account/hooks';
import { TripPlannerApiService } from '../services/tripPlannerApiService';
import { useHomeConfig } from '../config/HomeConfigProvider';
import { useHomepageBlocks } from './useHomepage';

export const tripPlannerKeys = {
  upcomingTrip: (locale: string, currency: string) =>
    [
      QUERY_PERSIST_CACHE,
      'tripPlanner',
      'upcomingTrip',
      locale,
      currency,
    ] as const,
};

export function useTripPlannerApiService(): TripPlannerApiService {
  const { config } = useHomeConfig();
  return useMemo(() => {
    return new TripPlannerApiService(config.homeTripPlannerApi);
  }, [config.homeTripPlannerApi]);
}

export const useUpcomingTrips = (caller: string = 'tripPlanner') => {
  const api = useTripPlannerApiService();
  const accessTokenQuery = useAccessToken(caller);
  const isLoggedInQuery = useIsLoggedIn();
  const appConfig = useAppConfig();
  const { currentLanguage } = useLocale();
  const { responseCurrency } = useHomepageBlocks();

  const currency = (
    appConfig.config.currency ||
    responseCurrency ||
    'USD'
  ).toUpperCase();
  const locale = appConfig.config.locale || 'en-GB';

  const query = useQuery({
    queryKey: tripPlannerKeys.upcomingTrip(locale, currency),
    queryFn: async () => {
      if (!accessTokenQuery.data) {
        throw new Error('Access token not available');
      }

      const result = await api.fetchUpcomingTrip(
        accessTokenQuery.data,
        locale,
        currency,
      );
      return result;
    },

    staleTime: 10 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    retry: 2,
    networkMode: 'offlineFirst',

    enabled:
      isLoggedInQuery.isSuccess &&
      isLoggedInQuery.isLoggedIn &&
      accessTokenQuery.isTokenValid &&
      Boolean(appConfig.config.currency || responseCurrency),
  });

  const hasEvents = Boolean(
    query.data?.data?.events && query.data.data.events.length > 0,
  );
  const shouldShow = hasEvents || query.isError || query.isLoading;

  return {
    ...query,
    shouldShow,
    hasEvents,
  };
};

export const useFetchUpcomingTrip = useUpcomingTrips;
